import { ChangeEvent, FC, KeyboardEvent, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGrid as DataGridComponent } from '@robocorp/components';
import { IconCloseSmall } from '@robocorp/icons';

type Item = {
  key: string;
  value: string | null;
};
const EMPTY_GRID_ITEM: Item = { key: '', value: '' };

type Error = { message: string };

type Props = {
  value: Item[];
  onChange: (value: Item[]) => void;
  onBlur?: (value: Item[]) => void;
  errors?: Record<number, Error> | Error[];
  hideValues?: boolean;
  withSecrets?: boolean;
};

const onKeyDown = (e: KeyboardEvent) => {
  if (e.code === 'Enter') {
    e.preventDefault();
  }
};

const Content: FC<Props> = ({ value, onChange, onBlur, errors, hideValues, withSecrets }) => {
  const { t } = useTranslation();

  const onItemBlur = () => onBlur?.(value);
  const onEntryRemove = (target: number) =>
    onChange(value.filter((_item, index) => target !== index));

  const onEntryChange =
    (target: number, changingKey?: boolean) => (e: ChangeEvent<HTMLInputElement>) => {
      const updated = [...value];
      const entry = updated[target] ?? {};
      updated[target] = changingKey
        ? { key: e.target.value, value: entry?.value ?? '' }
        : { key: entry?.key ?? '', value: e.target.value };

      return onChange(updated);
    };

  const ValueInputComponent =
    !hideValues && withSecrets ? DataGridComponent.SecretInput : DataGridComponent.Input;

  return (
    <DataGridComponent columns={['auto', 'auto', 'min-content']}>
      {[...value, EMPTY_GRID_ITEM].map(({ key, value: itemValue }, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <DataGridComponent.Row key={index}>
          <DataGridComponent.Input
            aria-label="Key"
            value={key ?? ''}
            placeholder={t('key')}
            error={errors?.[index] ? t(errors[index].message) : undefined}
            onChange={onEntryChange(index, true)}
            onBlur={onItemBlur}
            onKeyDown={onKeyDown}
          />
          <ValueInputComponent
            aria-label="Value"
            value={itemValue ?? ''}
            placeholder={hideValues && itemValue === null ? '••••••••' : t('value')}
            onChange={onEntryChange(index, false)}
            onBlur={onItemBlur}
            onKeyDown={onKeyDown}
          />
          {value.length !== index && (
            <DataGridComponent.Button
              type="button"
              onClick={() => onEntryRemove(index)}
              icon={IconCloseSmall}
              aria-label="Remove entry"
            />
          )}
        </DataGridComponent.Row>
      ))}
    </DataGridComponent>
  );
};

export const DataGrid = memo(Content);
